import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { useSubscriptionStore } from '../../store/subscriptionStore';
import { createPayment, getPaymentStatus } from '../../api/payment.api';
import { applyPromocode } from '../../api/promocode.api';
import type { PromocodeResult } from '../../types/promocode';
import { formatPrice } from '../../utils/formatPrice';
import { getApiErrorMessage } from '../../utils/apiError';
import styles from './PaymentPage.module.css';

type PageState = 'loading' | 'confirm' | 'processing' | 'success' | 'error';

export function PaymentPage() {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const { plans, fetchPlans } = useSubscriptionStore();
  const [state, setState] = useState<PageState>('loading');
  const [errorMsg, setErrorMsg] = useState('');

  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [appliedPromo, setAppliedPromo] = useState<PromocodeResult | null>(null);

  const plan = plans.find((p) => p.id === planId);

  useEffect(() => {
    if (plans.length === 0) {
      fetchPlans().then(() => setState('confirm'));
    } else {
      setState('confirm');
    }
  }, [plans.length, fetchPlans]);

  const hasPercent = !!appliedPromo && appliedPromo.discountPercent != null;
  const hasDays = !!appliedPromo && appliedPromo.discountDays != null;

  const finalPriceKopecks =
    plan && hasPercent && appliedPromo?.discountPercent != null
      ? Math.round((plan.priceKopecks * (100 - appliedPromo.discountPercent)) / 100)
      : plan?.priceKopecks ?? 0;

  const handleApplyPromo = async () => {
    const code = promoCode.trim();
    if (!code) return;
    setPromoLoading(true);
    setPromoError('');
    try {
      const result = await applyPromocode(code);
      setAppliedPromo(result);
    } catch (err) {
      setAppliedPromo(null);
      setPromoError(getApiErrorMessage(err, 'Не удалось применить промокод'));
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoError('');
    setPromoCode('');
  };

  const handlePay = async () => {
    if (!planId) return;
    setState('processing');
    try {
      const returnUrl = `${window.location.origin}/dashboard/payment/success`;
      const resp = await createPayment(planId, returnUrl, appliedPromo?.code);

      if (resp.confirmationUrl) {
        // Redirect to YooKassa (or stub page)
        window.location.href = resp.confirmationUrl;
        return;
      }

      // If no redirect (stub mode), poll for status
      const paymentId = resp.paymentId;
      let attempts = 0;
      const pollInterval = setInterval(async () => {
        attempts++;
        try {
          const payment = await getPaymentStatus(paymentId);
          if (payment.status === 'succeeded') {
            clearInterval(pollInterval);
            setState('success');
            setTimeout(() => navigate('/dashboard'), 2000);
          } else if (payment.status === 'cancelled' || attempts > 30) {
            clearInterval(pollInterval);
            setState('error');
            setErrorMsg('Оплата не прошла');
          }
        } catch {
          if (attempts > 30) {
            clearInterval(pollInterval);
            setState('error');
            setErrorMsg('Не удалось проверить статус оплаты');
          }
        }
      }, 2000);
    } catch (err) {
      setState('error');
      setErrorMsg(getApiErrorMessage(err, 'Ошибка при создании платежа'));
    }
  };

  if (state === 'loading') {
    return (
      <div className={styles.page}>
        <Spinner size="lg" />
      </div>
    );
  }

  if (state === 'success') {
    return (
      <div className={styles.page}>
        <div className={styles.status}>
          <div className={styles.statusIcon}>&#10003;</div>
          <div className={styles.statusText}>Оплата прошла успешно!</div>
          <div className={styles.statusSub}>Перенаправляем в личный кабинет...</div>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className={styles.page}>
        <div className={styles.status}>
          <div className={styles.statusIcon}>&#10007;</div>
          <div className={styles.error}>{errorMsg}</div>
          <Button onClick={() => setState('confirm')}>Попробовать снова</Button>
        </div>
      </div>
    );
  }

  if (state === 'processing') {
    return (
      <div className={styles.page}>
        <div className={styles.status}>
          <Spinner size="lg" />
          <div className={styles.statusText} style={{ marginTop: '1rem' }}>
            Обрабатываем оплату...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Оплата</h1>
      {plan && (
        <div className={styles.summary}>
          <div className={styles.planName}>{plan.name}</div>
          {hasPercent ? (
            <div className={styles.priceRow}>
              <span className={styles.oldPrice}>{formatPrice(plan.priceKopecks)}</span>
              <span className={styles.planPrice}>{formatPrice(finalPriceKopecks)}</span>
            </div>
          ) : (
            <div className={styles.planPrice}>{formatPrice(plan.priceKopecks)}</div>
          )}
        </div>
      )}

      <div className={styles.promoSection}>
        {appliedPromo ? (
          <div className={styles.promoApplied}>
            <div className={styles.promoAppliedText}>
              <span className={styles.promoCodeLabel}>{appliedPromo.code}</span>
              {hasPercent && (
                <span className={styles.promoBonus}>
                  Скидка {appliedPromo.discountPercent}%
                </span>
              )}
              {hasDays && (
                <span className={styles.promoBonus}>
                  +{appliedPromo.discountDays} дней к подписке
                </span>
              )}
            </div>
            {hasDays && (
              <div className={styles.promoNote}>Стоимость тарифа не меняется</div>
            )}
            <button type="button" className={styles.promoRemove} onClick={handleRemovePromo}>
              &#10005; убрать
            </button>
          </div>
        ) : (
          <>
            <div className={styles.promoInputRow}>
              <input
                className={styles.promoInput}
                type="text"
                placeholder="Промокод"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleApplyPromo();
                  }
                }}
                disabled={promoLoading}
                aria-label="Промокод"
              />
              <Button
                variant="secondary"
                onClick={handleApplyPromo}
                loading={promoLoading}
                disabled={!promoCode.trim() || promoLoading}
              >
                Применить
              </Button>
            </div>
            {promoError && <div className={styles.promoError}>{promoError}</div>}
          </>
        )}
      </div>

      <Button fullWidth onClick={handlePay} size="lg">
        Оплатить {plan ? formatPrice(finalPriceKopecks) : ''}
      </Button>
      <div style={{ marginTop: 'var(--space-3)' }}>
        <Button variant="secondary" onClick={() => navigate('/dashboard/plans')}>
          Назад к тарифам
        </Button>
      </div>
    </div>
  );
}
